import type { MenuContext, VirtualPackageKind } from "@magic-md/extension-sdk";
import type { FileTreeNode } from "../store/editor-store";

const DOCUMENT_BUNDLE_SUFFIX = ".magic-md";

const DOCUMENT_PACKAGE_KINDS = new Set<VirtualPackageKind>([
    "assets",
    "configs",
    "publish",
    "history",
    "outputs",
    "annotations",
    "watches",
    "traces",
]);

type FileNodeMenuContextInput = Pick<
    FileTreeNode,
    "name" | "path" | "isDirectory" | "isVirtual"
>;

function normalizePath(path: string): string {
    const normalized = path.replace(/\\/g, "/");
    return normalized.length > 1 ? normalized.replace(/\/+$/, "") : normalized;
}

function splitPackagePath(rest: string | undefined): {
    packageName?: string;
    relativePath?: string;
    kind: VirtualPackageKind;
} {
    const parts = (rest ?? "").split("/").filter(Boolean);
    const packageName = parts[0];
    if (!packageName) {
        return { kind: "document-bundle" };
    }
    const kind = DOCUMENT_PACKAGE_KINDS.has(packageName as VirtualPackageKind)
        ? (packageName as VirtualPackageKind)
        : "unknown";
    const relativePath = parts.slice(1).join("/");
    return {
        kind,
        packageName,
        relativePath: relativePath || undefined,
    };
}

function documentPathFromBundlePath(bundlePath: string): string {
    return `${bundlePath.slice(0, -DOCUMENT_BUNDLE_SUFFIX.length)}.md`;
}

export function buildVirtualPackageMenuContext(
    node: FileNodeMenuContextInput,
): MenuContext["virtual"] | undefined {
    if (!node.isVirtual) return undefined;

    const physicalPath = normalizePath(node.path);
    const folderPublishMatch = /^(.*)\/\.magic-md\/publish(?:\/(.*))?$/i.exec(
        physicalPath,
    );
    if (folderPublishMatch) {
        return {
            isVirtual: true,
            kind: "folder-publish",
            physicalPath,
            nodeName: node.name,
            isDirectory: node.isDirectory,
            packageName: "publish",
            ownerFolderPath: folderPublishMatch[1],
            relativePath: folderPublishMatch[2] || undefined,
        };
    }

    const documentBundleMatch = /^(.*\.magic-md)(?:\/(.*))?$/i.exec(physicalPath);
    if (!documentBundleMatch) {
        return {
            isVirtual: true,
            kind: "unknown",
            physicalPath,
            nodeName: node.name,
            isDirectory: node.isDirectory,
        };
    }

    const ownerBundlePath = documentBundleMatch[1];
    const packagePath = splitPackagePath(documentBundleMatch[2]);
    return {
        isVirtual: true,
        physicalPath,
        nodeName: node.name,
        isDirectory: node.isDirectory,
        ownerBundlePath,
        ownerDocumentPath: documentPathFromBundlePath(ownerBundlePath),
        ...packagePath,
    };
}

export function buildFileNodeMenuContext(
    node: FileNodeMenuContextInput,
    options: { tabId?: string } = {},
): MenuContext {
    const context: MenuContext = {
        surface: "file",
        path: node.path,
        tabId: options.tabId,
        targetKind: node.isDirectory ? "folder" : "file",
    };
    const virtual = buildVirtualPackageMenuContext(node);
    if (virtual) context.virtual = virtual;
    return context;
}
